/**
 * Timed drill (PLAN-Z M5/M7, PLAN.md §3): an endless mixed stream with a
 * count-up timer and per-answer feedback — correct/incorrect, the right
 * answer, and the generator's own explanation. Attempts are persisted to
 * IndexedDB the instant they are submitted.
 *
 * Figure items produce TWO scored responses (one per blank) sharing one
 * solve duration; a half-right answer earns half the credit rather than
 * nothing (PLAN.md §2c).
 *
 * Difficulty mix uses the cold-start weights (20/40/40) uniformly across the
 * subtests; the adaptive selector replaces this in M10.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  generateEquations,
  type EquationsAnswer,
  type EquationsPrompt,
} from '../generators/equations.ts'
import {
  generateFigures,
  type FiguresAnswer,
  type FiguresPrompt,
} from '../generators/figures.ts'
import {
  generateLatin,
  type LatinAnswer as LatinAnswerData,
  type LatinPrompt,
} from '../generators/latin.ts'
import { pickNext } from '../adaptive/selector.ts'
import type { Difficulty, Question, Subtest } from '../generators/types.ts'
import { loadAttempts, newId, saveAttempt, type Attempt } from '../data/local.ts'
import { pushAttempts } from '../data/sync.ts'
import { bucketStats, type BucketStats } from '../stats/compute.ts'
import { computeWeights } from '../adaptive/selector.ts'
import { QuestionCard } from '../render/QuestionCard.tsx'
import { EquationsInputs, EquationsSystem } from '../render/Equations.tsx'
import { LatinAnswer, LatinGrid } from '../render/LatinGrid.tsx'
import { FiguresOptions } from '../render/Figures.tsx'
import { FiguresStem } from '../render/FigureMatrix.tsx'
import { ReportPanel } from '../report/ReportPanel.tsx'
import { gradeEquations, gradeLatin } from '../render/grade.ts'

const SUBTESTS: Subtest[] = ['figures', 'equations', 'latin']
const DEBUG_ADAPTIVE = typeof window !== 'undefined' && window.location.search.includes('debug')

interface CurrentItem {
  itemId: string
  subtest: Subtest
  difficulty: Difficulty
  seed: number
  question: Question
  shownAt: number
  /** Wall-clock moment the question was first shown (drives the per-question timer). */
  startedAtWall: number
}

function makeItem(subtest: Subtest, difficulty: Difficulty, seed: number): Question {
  if (subtest === 'equations') return generateEquations(difficulty, seed)
  if (subtest === 'latin') return generateLatin(difficulty, seed)
  return generateFigures(difficulty, seed)
}

function nextItem(subtest: Subtest, difficulty: Difficulty, seed: number): CurrentItem {
  return {
    itemId: newId(),
    subtest,
    difficulty,
    seed,
    question: makeItem(subtest, difficulty, seed),
    shownAt: performance.now(),
    startedAtWall: Date.now(),
  }
}

interface Session {
  id: string
  startedAt: number
  /** Serves as the per-session seed counter: item n gets seed n. */
  served: number
  attempts: Attempt[]
}

function newSession(): Session {
  return { id: newId(), startedAt: Date.now(), served: 0, attempts: [] }
}

interface Graded {
  correct: boolean
  /** Figure items only: exactly one of the two blanks right. */
  partial: boolean
  submittedAnswer: string
  rightAnswer: string
  attempts: Attempt[]
}

interface DrillState {
  session: Session
  current: CurrentItem
  phase: 'drill' | 'summary'
  graded: Graded | null
  eqValues: Record<string, string>
  latinPick: string | null
  figPicks: [number | null, number | null]
  endedAt: number | null
}

function freshState(): DrillState {
  const session = newSession()
  // no history yet → cold-start weights (20/40/40, uniform subtests)
  const { subtest, difficulty } = pickNext([], SUBTESTS)
  return {
    session,
    current: nextItem(subtest, difficulty, 1),
    phase: 'drill',
    graded: null,
    eqValues: {},
    latinPick: null,
    figPicks: [null, null],
    endedAt: null,
  }
}

function fmtClock(ms: number): string {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function fmtSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`
}

/** Ticking wall-clock reading; active while `active` is true. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(timer)
  }, [active])
  return now
}

export function DrillPage() {
  const [state, setState] = useState<DrillState>(freshState)
  const [history, setHistory] = useState<Attempt[] | null>(null)
  const { session, current, phase, graded, eqValues, latinPick, figPicks, endedAt } = state
  const now = useNow(endedAt === null)
  const questionElapsedMs =
    graded !== null
      ? (graded.attempts[0]?.durationMs ?? 0)
      : Math.max(0, now - current.startedAtWall)

  useEffect(() => {
    void loadAttempts().then(setHistory)
  }, [])

  const sessionStats: BucketStats = useMemo(
    () => bucketStats(session.attempts),
    [session.attempts],
  )

  const eqPrompt: EquationsPrompt | null =
    current.subtest === 'equations' ? (current.question.prompt as EquationsPrompt) : null
  const eqAnswer: Record<string, number> | null =
    current.subtest === 'equations' ? (current.question.answer as EquationsAnswer).values : null
  const latinPrompt: LatinPrompt | null =
    current.subtest === 'latin' ? (current.question.prompt as LatinPrompt) : null
  const latinAnswer: string | null =
    current.subtest === 'latin' ? (current.question.answer as LatinAnswerData).letter : null
  const figPrompt: FiguresPrompt | null =
    current.subtest === 'figures' ? (current.question.prompt as FiguresPrompt) : null
  const figAnswer: FiguresAnswer | null =
    current.subtest === 'figures' ? (current.question.answer as FiguresAnswer) : null

  const eqComplete = eqPrompt !== null && eqPrompt.letters.every((L) => (eqValues[L] ?? '') !== '')
  const figComplete = figPicks[0] !== null && figPicks[1] !== null
  const canSubmit = graded === null && (eqComplete || latinPick !== null || figComplete)

  const submit = () => {
    if (graded !== null) return
    const durationMs = Math.round(performance.now() - current.shownAt)
    const answeredAt = new Date().toISOString()
    let attempts: Attempt[] = []
    let correct = false
    let partial = false
    let submittedAnswer = ''
    let rightAnswer = ''

    if (current.subtest === 'equations' && eqPrompt && eqAnswer) {
      const grade = gradeEquations(eqPrompt, eqAnswer, eqValues)
      correct = grade.allCorrect
      submittedAnswer = eqPrompt.letters.map((L) => `${L}=${eqValues[L] ?? ''}`).join(', ')
      rightAnswer = eqPrompt.letters.map((L) => `${L}=${eqAnswer[L]}`).join(', ')
      attempts = [
        {
          id: newId(),
          sessionId: session.id,
          itemId: current.itemId,
          subtest: current.subtest,
          difficulty: current.difficulty,
          seed: current.seed,
          correct,
          subAnswers: { perLetter: grade.perLetter, values: eqValues },
          durationMs,
          answeredAt,
        },
      ]
    } else if (current.subtest === 'latin' && latinAnswer !== null) {
      correct = gradeLatin(latinAnswer, latinPick)
      submittedAnswer = latinPick ?? '—'
      rightAnswer = latinAnswer
      attempts = [
        {
          id: newId(),
          sessionId: session.id,
          itemId: current.itemId,
          subtest: current.subtest,
          difficulty: current.difficulty,
          seed: current.seed,
          correct,
          subAnswers: { letter: latinPick },
          durationMs,
          answeredAt,
        },
      ]
    } else if (current.subtest === 'figures' && figAnswer) {
      const [c1, c2] = figAnswer.correct
      const b1 = figPicks[0] === c1
      const b2 = figPicks[1] === c2
      correct = b1 && b2
      partial = b1 !== b2
      submittedAnswer = `Next: option ${(figPicks[0] ?? 0) + 1} · After that: option ${(figPicks[1] ?? 0) + 1}`
      rightAnswer = `Next: option ${c1 + 1} · After that: option ${c2 + 1}`
      attempts = [1, 2].map((blank) => ({
        id: newId(),
        sessionId: session.id,
        itemId: current.itemId,
        subtest: current.subtest,
        difficulty: current.difficulty,
        seed: current.seed,
        correct: blank === 1 ? b1 : b2,
        subAnswers: { blank, pick: blank === 1 ? figPicks[0] : figPicks[1] },
        durationMs,
        answeredAt,
      }))
    } else {
      return
    }

    // Written the instant the answer is given (PLAN-Z M5); the Supabase push
    // (M9) is fire-and-forget — failures ride the next syncNow().
    void Promise.all(attempts.map(saveAttempt))
    void pushAttempts(attempts)

    setState((prev) => ({
      ...prev,
      session: {
        ...prev.session,
        served: prev.session.served + 1,
        attempts: [...prev.session.attempts, ...attempts],
      },
      graded: { correct, partial, submittedAnswer, rightAnswer, attempts },
    }))
  }

  const nextQuestion = () => {
    // Adaptive pick (M10) over the full history plus this session. Exam block
    // mode does NOT use the selector — its ladder is fixed.
    const pool = [...(history ?? []), ...session.attempts]
    const pick = pickNext(pool, SUBTESTS)
    if (DEBUG_ADAPTIVE) {
      console.debug(
        '[adaptive] weights',
        [...computeWeights(pool, SUBTESTS).entries()].map(([k, v]) => `${k}=${v.toFixed(4)}`),
      )
    }
    setState((prev) => ({
      ...prev,
      current: nextItem(pick.subtest, pick.difficulty, prev.session.served + 1),
      graded: null,
      eqValues: {},
      latinPick: null,
      figPicks: [null, null],
    }))
  }

  if (phase === 'summary') {
    return (
      <div className="drill-page">
        <section className="question-card">
          <h2 className="question-card-title">Session summary</h2>
          {session.attempts.length === 0 ? (
            <p>No questions answered.</p>
          ) : (
            <div className="drill-summary">
              <p>
                <strong>{session.attempts.length}</strong> answered ·{' '}
                <strong>{sessionStats.correct}</strong> correct ·{' '}
                <strong>{Math.round(sessionStats.accuracy * 100)}%</strong> accuracy
              </p>
              <p>
                Average {fmtSeconds(sessionStats.avgMs)} per question · typical{' '}
                {fmtSeconds(sessionStats.medianMs)}{' '}
                <span className="muted">(exam budget: 75 s per question)</span>
              </p>
              <p className="muted">
                Every attempt was saved on this device — the stats dashboard (M8) builds on them.
              </p>
            </div>
          )}
          <button type="button" className="question-submit" onClick={() => setState(freshState())}>
            New session
          </button>
        </section>
      </div>
    )
  }

  const meta = `${current.subtest} · ${current.difficulty} · seed ${current.seed}`

  return (
    <div className="drill-page">
      <div className="drill-header">
        <span className="drill-timer" aria-label="Timer">
          ⏱ {fmtClock(questionElapsedMs)}
        </span>
        <span className="drill-count">{session.attempts.length} answered</span>
        <button
          type="button"
          className="drill-exit"
          onClick={() => setState((prev) => ({ ...prev, phase: 'summary', endedAt: Date.now() }))}
        >
          End session
        </button>
      </div>
      <QuestionCard
        title={meta}
        prompt={
          current.subtest === 'equations' && eqPrompt ? (
            <EquationsSystem prompt={eqPrompt} />
          ) : current.subtest === 'latin' && latinPrompt ? (
            <LatinGrid prompt={latinPrompt} />
          ) : figPrompt ? (
            <FiguresStem prompt={figPrompt} />
          ) : null
        }
        answer={
          current.subtest === 'equations' && eqPrompt ? (
            <EquationsInputs
              prompt={eqPrompt}
              values={eqValues}
              disabled={graded !== null}
              onChange={(letter, value) =>
                setState((prev) => ({ ...prev, eqValues: { ...prev.eqValues, [letter]: value } }))
              }
              onSubmitLast={() => {
                if (eqComplete) submit()
              }}
            />
          ) : current.subtest === 'latin' ? (
            <LatinAnswer
              selected={latinPick}
              disabled={graded !== null}
              correctLetter={graded !== null ? latinAnswer : null}
              onSelect={(letter) => setState((prev) => ({ ...prev, latinPick: letter }))}
            />
          ) : figPrompt && figAnswer ? (
            <FiguresOptions
              options={figPrompt.options}
              picks={figPicks}
              disabled={graded !== null}
              correct={graded !== null ? figAnswer.correct : null}
              onPick={(blank, index) =>
                setState((prev) => {
                  const next: [number | null, number | null] = [...prev.figPicks]
                  next[blank] = index
                  return { ...prev, figPicks: next }
                })
              }
            />
          ) : null
        }
        onSubmit={submit}
        submitDisabled={!canSubmit}
        feedback={
          graded === null ? undefined : (
            <div className="drill-feedback">
              <p
                className={
                  graded.correct
                    ? 'drill-verdict correct'
                    : graded.partial
                      ? 'drill-verdict'
                      : 'drill-verdict wrong'
                }
              >
                {graded.correct ? 'Correct.' : graded.partial ? 'Half right.' : 'Wrong.'} Answer:{' '}
                {graded.rightAnswer}
              </p>
              <p className="muted">You answered: {graded.submittedAnswer}</p>
              {current.question.explanation.length > 0 ? (
                <div>
                  <p className="muted">How it breaks down:</p>
                  <ol className="drill-explanation">
                    {current.question.explanation.map((step, i) => (
                      <li key={i}>
                        {step.text}
                        {step.lines ? (
                          <span className="step-lines">
                            {step.lines.map((line, j) => (
                              <span key={j}>{line}</span>
                            ))}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
              <button type="button" className="question-submit" onClick={nextQuestion}>
                Next question →
              </button>
              <ReportPanel
                key={current.itemId}
                input={{
                  question: current.question,
                  difficulty: current.difficulty,
                  subAnswers: graded.attempts[0]?.subAnswers ?? {},
                  submittedAnswer: graded.submittedAnswer,
                  rightAnswer: graded.rightAnswer,
                  sessionId: session.id,
                  itemId: current.itemId,
                }}
              />
            </div>
          )
        }
      />
    </div>
  )
}
